/**
 * 사업장 그림의 배치 — 지역 › 공장 › 건물 영역이 **각자 부모 안의 자리와 크기**를 갖는다(모니터링 ▸ 사업장 현황).
 *
 * - 정한 적 없는 영역은 자동 배치다. 아래에서부터 센다: 건물은 화면 노드 수로, 공장은 건물을 세로로 쌓아, 지역은
 *   공장을 대략 정사각으로, 그림은 지역을 대략 정사각으로.
 * - 사람이 옮기거나 키운 영역은 그 값(`SavedBoxes`, 계정별 저장)을 쓴다. 좌표는 **부모 상자의 왼쪽 위 기준**이다
 *   (지역은 그림 기준).
 * - 두 규칙이 늘 지켜진다: **자식은 부모의 안쪽(머리줄 아래, 여백 안)을 벗어나지 않고**(옮기기·키우기에서 막는다,
 *   `clampMove`·`clampResize`), **부모는 자식들을 감싸는 크기보다 작아지지 않는다**(저장값이 그보다 작으면 늘린다).
 *   건물은 그 폭에 화면 노드를 줄 바꿔 담을 높이보다 낮아지지 않는다.
 *
 * 그림 안의 크기는 고정이다(노드 172×144) — 크게 볼지는 카메라 배율이 정한다.
 */
import type { BuildingNode, FactoryNode, RegionNode } from './sites.js';

export const NODE_W = 172;
export const NODE_H = 144;
export const NODE_GAP = 10;
/** 영역 안쪽 여백 — 자식은 이 안에만 놓인다. */
export const PAD = 12;
/** 머리줄 높이 — 자식은 이 아래에만 놓인다. */
export const HEAD = { region: 48, factory: 44, building: 32 } as const;
/** 화면 목록을 못 받은 건물의 안내 한 줄 높이. */
const NOTE_H = 40;
/** 그림에서 지역 사이 간격. */
const WORLD_GAP = 56;
const CHILD_GAP = 16;
/** 머리줄 글자가 읽힐 최소 폭. */
export const MIN_W = { region: 300, factory: 280, building: NODE_W + PAD * 2 } as const;
/** 자동 배치에서 건물 한 줄의 노드 수. */
const AUTO_COLUMNS = 4;

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** 사람이 정한 자리·크기 — 열쇠는 `regionKeyOf`·`factoryKeyOf`·`buildingKeyOf`. */
export type SavedBoxes = Record<string, Box>;

export type AreaKind = 'region' | 'factory' | 'building';

export interface LaidBuilding {
  key: string;
  box: Box;
  building: BuildingNode;
}

export interface LaidFactory {
  key: string;
  box: Box;
  factory: FactoryNode;
  buildings: LaidBuilding[];
}

export interface LaidRegion {
  key: string;
  box: Box;
  region: RegionNode;
  factories: LaidFactory[];
}

export interface SiteLayout {
  regions: LaidRegion[];
  width: number;
  height: number;
}

// 이름에 무엇이 들어와도 갈리지 않게 JSON 배열로 묶는다(지역·공장 이름은 사람이 적는다).
export const regionKeyOf = (region: Pick<RegionNode, 'name'>): string => `r:${JSON.stringify([region.name])}`;
export const factoryKeyOf = (factory: Pick<FactoryNode, 'region' | 'name'>): string => `f:${JSON.stringify([factory.region, factory.name])}`;
export const buildingKeyOf = (building: Pick<BuildingNode, 'source' | 'location'>): string =>
  `b:${JSON.stringify([building.source.id, building.location])}`;

/** 그 폭의 건물에 노드가 한 줄에 몇 개 들어가나. */
export function columnsFor(width: number): number {
  return Math.max(1, Math.floor((width - PAD * 2 + NODE_GAP) / (NODE_W + NODE_GAP)));
}

/** 그 폭의 건물이 노드를 다 담을 최소 높이. */
export function buildingMinHeight(building: BuildingNode, width: number): number {
  const count = building.displays?.length ?? 0;
  if (count === 0) return HEAD.building + NOTE_H + PAD;
  const rows = Math.ceil(count / columnsFor(width));
  return HEAD.building + rows * NODE_H + (rows - 1) * NODE_GAP + PAD;
}

const extentOf = (boxes: readonly Box[]): { w: number; h: number } => ({
  w: Math.max(0, ...boxes.map((box) => box.x + box.w)),
  h: Math.max(0, ...boxes.map((box) => box.y + box.h)),
});

/** 자식들을 감싸는 부모의 최소 크기 — 자식의 오른쪽·아래 끝에 여백을 더한다. */
export function minSizeAround(children: readonly Box[], kind: Exclude<AreaKind, 'building'>): { w: number; h: number } {
  const extent = extentOf(children);
  return { w: Math.max(MIN_W[kind], extent.w + PAD), h: Math.max(HEAD[kind] + PAD, extent.h + PAD) };
}

function layBuilding(building: BuildingNode, saved: SavedBoxes, at: { x: number; y: number }): LaidBuilding {
  const key = buildingKeyOf(building);
  const own = saved[key];
  const count = building.displays?.length ?? 0;
  const autoW = PAD * 2 + Math.max(2, Math.min(AUTO_COLUMNS, count)) * NODE_W + (Math.max(2, Math.min(AUTO_COLUMNS, count)) - 1) * NODE_GAP;
  const w = Math.max(MIN_W.building, own?.w ?? autoW);
  const h = Math.max(buildingMinHeight(building, w), own?.h ?? 0);
  return { key, building, box: { x: own?.x ?? at.x, y: own?.y ?? at.y, w, h } };
}

function layFactory(factory: FactoryNode, saved: SavedBoxes, at: { x: number; y: number }): LaidFactory {
  const key = factoryKeyOf(factory);
  const buildings: LaidBuilding[] = [];
  // 정하지 않은 건물은 이미 놓인 것들 아래로 쌓는다 — 옮겨 둔 건물과 겹치지 않게.
  let cursor: number = HEAD.factory;
  for (const building of factory.buildings) {
    const laid = layBuilding(building, saved, { x: PAD, y: cursor });
    buildings.push(laid);
    cursor = Math.max(cursor, laid.box.y + laid.box.h + CHILD_GAP);
  }
  const own = saved[key];
  const min = minSizeAround(buildings.map((item) => item.box), 'factory');
  const w = Math.max(min.w, own?.w ?? 0);
  const h = Math.max(min.h, own?.h ?? 0);
  return { key, factory, buildings, box: { x: own?.x ?? at.x, y: own?.y ?? at.y, w, h } };
}

/**
 * 크기를 아는 것들을 대략 정사각 격자로 — 열마다 가장 넓은 것, 줄마다 가장 높은 것에 맞춘다. 정한 자리가 있는
 * 것은 그 자리를 쓰고 격자 칸만 비워 둔다.
 */
function grid<T extends { box: Box }>(items: T[], columns: number, origin: { x: number; y: number }, gap: number, fixed: (item: T) => boolean): void {
  const widths: number[] = [];
  const heights: number[] = [];
  items.forEach((item, index) => {
    const col = index % columns;
    const row = Math.floor(index / columns);
    widths[col] = Math.max(widths[col] ?? 0, item.box.w);
    heights[row] = Math.max(heights[row] ?? 0, item.box.h);
  });
  items.forEach((item, index) => {
    if (fixed(item)) return;
    const col = index % columns;
    const row = Math.floor(index / columns);
    item.box.x = origin.x + widths.slice(0, col).reduce((sum, value) => sum + value + gap, 0);
    item.box.y = origin.y + heights.slice(0, row).reduce((sum, value) => sum + value + gap, 0);
  });
}

export function computeSiteLayout(tree: readonly RegionNode[], saved: SavedBoxes): SiteLayout {
  const regions: LaidRegion[] = tree.map((region) => {
    const key = regionKeyOf(region);
    const factories = region.factories.map((factory) => layFactory(factory, saved, { x: 0, y: 0 }));
    grid(factories, Math.min(3, Math.max(1, Math.ceil(Math.sqrt(factories.length)))), { x: PAD, y: HEAD.region }, CHILD_GAP, (item) => Boolean(saved[item.key]));
    const own = saved[key];
    const min = minSizeAround(factories.map((item) => item.box), 'region');
    return { key, region, factories, box: { x: own?.x ?? 0, y: own?.y ?? 0, w: Math.max(min.w, own?.w ?? 0), h: Math.max(min.h, own?.h ?? 0) } };
  });
  grid(regions, Math.max(1, Math.ceil(Math.sqrt(regions.length))), { x: 0, y: 0 }, WORLD_GAP, (item) => Boolean(saved[item.key]));
  const extent = extentOf(regions.map((item) => item.box));
  return { regions, width: extent.w, height: extent.h };
}

/**
 * 옮긴 자리를 부모의 안쪽으로 — 머리줄 아래, 여백 안. 부모가 없으면(지역) 그림의 왼쪽 위보다 앞으로 가지 않는다.
 */
export function clampMove(box: Box, parent: { w: number; h: number; head: number } | null): Box {
  if (!parent) return { ...box, x: Math.max(0, Math.round(box.x)), y: Math.max(0, Math.round(box.y)) };
  const x = Math.min(Math.max(PAD, box.x), parent.w - PAD - box.w);
  const y = Math.min(Math.max(parent.head, box.y), parent.h - PAD - box.h);
  return { ...box, x: Math.round(Math.max(PAD, x)), y: Math.round(Math.max(parent.head, y)) };
}

/**
 * 바꾼 크기를 두 끝 사이로 — 작게는 자기 내용(자식들·노드)을 담을 만큼, 크게는 부모의 안쪽 끝까지.
 * `minHeightFor` 는 폭에 따라 최소 높이가 바뀌는 건물(노드 줄 바꿈)을 위해 폭을 받는다.
 */
export function clampResize(
  box: Box,
  min: { w: number; minHeightFor: (width: number) => number },
  parent: { w: number; h: number } | null,
  /** 지금의(맞는) 상자 — 어떤 폭으로도 부모 안에 안 들어가면 이것을 그대로 둔다. */
  previous?: Box,
): Box {
  const maxW = parent ? parent.w - PAD - box.x : Number.POSITIVE_INFINITY;
  const maxH = parent ? parent.h - PAD - box.y : Number.POSITIVE_INFINITY;
  let w = Math.round(Math.max(min.w, Math.min(maxW, box.w)));
  /*
   * 폭을 줄이면 노드가 줄을 바꿔 최소 높이가 자란다. 그 높이가 부모 안에 안 들어가면 폭을 덜 줄인다 — 노드 한 칸씩
   * 넓혀 보며 들어가는 폭을 찾는다(부모 안쪽 끝까지). 끝까지 안 되면 크기를 바꾸지 않는다.
   */
  while (min.minHeightFor(w) > maxH && w + NODE_W + NODE_GAP <= maxW) w += NODE_W + NODE_GAP;
  if (min.minHeightFor(w) > maxH) return previous ?? box;
  const h = Math.round(Math.max(min.minHeightFor(w), Math.min(maxH, box.h)));
  return { ...box, w, h };
}
